import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { DividerModule } from 'primeng/divider';
import { ImageModule } from 'primeng/image';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { finalize } from 'rxjs';
import { RilevamentoScambiDoppiService } from '@/core/service/rilevamento-doppi.service';
import { RilevamentoScambiSempliciService } from '@/core/service/rilevamento-semplici.service';
import { UtilsService } from '@/core/service/utils/utils.service';

type SKey = 's1' | 's2' | 's3' | 's4' | 's5' | 's6' | 's7' | 's8' | 's9' | 's10' | 's11' | 's12';

@Component({
    selector: 'app-misure-semplici-doppi',
    templateUrl: './misure-semplici-doppi.component.html',
    standalone: true,
    imports: [CommonModule, DividerModule, DatePickerModule, SelectModule, ImageModule, ReactiveFormsModule, TabsModule]
})
export class MisureSempliciDoppiComponent implements OnInit, OnChanges {
    @Input() form!: FormGroup;
    @Input() normativa!: any;
    @Input() prefix = '';
    @Input() deviatoioId!: any;
    @Input() formReady = false;
    @Input() isSemplice = false;

    sKeysSemplici: SKey[] = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8'];
    lastValues: Record<string, number | null> = {};
    trendIcons: Record<string, string | null> = {};
    dataReady = false;

    constructor(
        public utils: UtilsService,
        private rilevamentoDoppiService: RilevamentoScambiDoppiService,
        private rilevamentoSempliciService: RilevamentoScambiSempliciService,
        private cdr: ChangeDetectorRef
    ) {}

    // -------------------
    // 🔹 Lifecycle hooks
    // -------------------

    ngOnInit(): void {
        if (this.form) this.attachValueChangeListeners();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.formReady && this.deviatoioId) {
            this.loadLastRilevamento();
        }

        if (changes['form'] && this.form) {
            this.attachValueChangeListeners();
        }
    }

    // -------------------
    // 🔹 Data fetching
    // -------------------

    private loadLastRilevamento(): void {
        this.dataReady = false;

        const service = this.isSemplice ? this.rilevamentoSempliciService : this.rilevamentoDoppiService;

        service
            .getOrFetch(this.deviatoioId, () => service.list({ sort: 'id,desc', size: 5 }))
            .pipe(
                finalize(() => {
                    this.dataReady = true;
                    this.updateTrendIcons();
                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                next: (data) => this.populateLastValues(data),
                error: () => (this.lastValues = {})
            });
    }

    private populateLastValues(data: any[]): void {
        const last = data.find((d) => d.deviatoiRiepilogoGenerale?.id === this.deviatoioId?.id);

        if (!last) {
            this.lastValues = {};
            return;
        }

        const prefixNorm = this.prefix.replace(/_$/, '');
        const allFields = [...this.sKeysSemplici, 'a', 'b', 'c', 'd', 'p', 'q'];

        this.lastValues = Object.fromEntries(
            allFields.map((field) => {
                const keyWithPrefix = `${prefixNorm}${field.charAt(0).toUpperCase()}${field.slice(1)}`;
                const keyWithoutPrefix = field;
                return [field, last[keyWithPrefix] ?? last[keyWithoutPrefix] ?? null];
            })
        );
    }

    // -------------------
    // 🔹 Trend calculation
    // -------------------

    private calculateTrendIcon(field: string): string | null {
        if (!this.dataReady) return null;

        const lastValue = this.lastValues[field];
        if (lastValue == null) return null;

        const controlName = this.getFullControlName(field);
        const control = this.form.get(controlName);
        if (!control) return null;

        const current = Number(control.value);
        const prev = Number(lastValue);

        if (isNaN(current) || isNaN(prev)) return 'pi pi-equals text-gray-400';
        if (current > prev) return 'pi pi-arrow-up text-green-500';
        if (current < prev) return 'pi pi-arrow-down text-red-500';
        return 'pi pi-equals text-gray-400';
    }

    updateTrendIcons(): void {
        if (!this.form) return;

        const allFields = [...this.sKeysSemplici, 'a', 'b', 'c', 'd', 'p', 'q'];
        for (const field of allFields) {
            this.trendIcons[field] = this.calculateTrendIcon(field);
        }

        this.cdr.markForCheck();
    }

    // -------------------
    // 🔹 Utility methods
    // -------------------

    private getFullControlName(field: string): string {
        if (this.form.get(field)) return field; // support both prefixed/unprefixed
        if (!this.prefix) return field;
        const controlName = this.prefix.endsWith('_') ? this.prefix + field : `${this.prefix}_${field}`;
        return controlName.replace(/^_/, '');
    }

    getInputRangeClass(field: string): string {
        const controlName = this.getFullControlName(field);
        return this.utils.getInputRangeClass(controlName, this.form, this.normativa);
    }

    getRuleClass(field: string): string {
        const controlName = this.getFullControlName(field);
        return this.utils.getRuleClass(controlName, this.form, this.normativa);
    }

    getTrendIcon(field: string): string | null {
        return this.trendIcons[field] ?? null;
    }

    // -------------------
    // 🔹 Form value listeners
    // -------------------

    private attachValueChangeListeners(): void {
        if (!this.form) return;

        const allFields = [...this.sKeysSemplici, 'a', 'b', 'c', 'd', 'p', 'q'];
        Object.keys(this.form.controls).forEach((key) => {
            const normalizedPrefix = this.prefix.endsWith('_') ? this.prefix : `${this.prefix}_`;
            const fieldName = key.replace(normalizedPrefix, '');

            if (!allFields.includes(fieldName as any)) return;

            this.form.get(key)?.valueChanges.subscribe(() => {
                if (!this.dataReady) return;
                this.trendIcons[fieldName] = this.calculateTrendIcon(fieldName);
                this.cdr.markForCheck();
            });
        });
    }
}
